import { toast } from "react-toastify";
import { PaginatedResult } from "models/queryable";
import ContinuingEducationRequest, { IContinuingEducationQueryParams } from "models/continuing-education";
import ContinuingEducationEnvironment from "models/continuing-education-environment";
import ContinuingEducationType from "models/continuing-education-type";
import ContinuingEducationTopic from "models/continuing-education-topic";
import ContinuingEducationFields from "Components/organisms/forms/continuing-education-form/types";
import { buildQueryString } from "utils/query-string";
import ContinuingEducationBudgetSummary from "models/continuing-education-budget";

export const getContinuingEducationRequests = async (query?: IContinuingEducationQueryParams): Promise<PaginatedResult<ContinuingEducationRequest> | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education${buildQueryString(query)}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data as PaginatedResult<ContinuingEducationRequest>
    } catch (err: any) {
        toast.error(err.message ?? "Error getting Continuing Education requests");
        return null;
    }
}

export const submitCreateContinuingEducationForm = async (vals: ContinuingEducationFields): Promise<ContinuingEducationRequest | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education`

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(vals),
            headers: {
                'Content-Type': 'application/json',
            }
        })

        if (!res.ok) {
            const errBody = await res.json()
            throw new Error(errBody.message);
        }

        toast.success("Continuing Education request submitted.")
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error submitting Continuing Education request");
        return null;
    }
}

export const downloadContinuingEducationCsv = async (query?: IContinuingEducationQueryParams): Promise<void> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education/export${buildQueryString(query)}`;
        const res = await fetch(url, { credentials: 'include', method: 'GET' });

        if (!res.ok) {
            const errBody = await res.json().catch(() => null);
            throw new Error(errBody?.message ?? 'Failed to export CSV');
        }

        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'continuing-education.csv';
        a.click();
        window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
        toast.error(err.message ?? 'Error downloading CSV');
    }
}

export const getContinuingEducationRequest = async (id: string): Promise<ContinuingEducationRequest | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education/${id}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data as ContinuingEducationRequest
    } catch (err: any) {
        toast.error(err.message ?? "Error getting Continuing Education request");
        return null;
    }
}

export const getContinuingEducationFormFields = async (): Promise<{
    environments: ContinuingEducationEnvironment[];
    types: ContinuingEducationType[];
    topics: ContinuingEducationTopic[];
} | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education/form-fields`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as {
            environments: ContinuingEducationEnvironment[];
            types: ContinuingEducationType[];
            topics: ContinuingEducationTopic[];
        };
    } catch (err: any) {
        toast.error(err.message ?? "Error getting Continuing Education form fields");
        return null;
    }
}

export const getContinuingEducationRemainingBudget = async (courseEndDate: string, excludeRequestIds?: string[]): Promise<number | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education/remaining-budget${buildQueryString({ courseEndDate, excludeRequestIds })}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.remainingBudget;
    } catch (err: any) {
        // Quiet -- called as the user fills out the date field, not worth a toast per keystroke.
        console.error('Error getting remaining budget:', err.message);
        return null;
    }
}

export const approveContinuingEducationRequest = async (id: string, comment: string | null): Promise<ContinuingEducationRequest | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education/${id}/approve`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PUT',
            body: JSON.stringify({ comment }),
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Request approved.")
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error approving Continuing Education request");
        return null;
    }
}

export const declineContinuingEducationRequest = async (id: string, comment: string): Promise<ContinuingEducationRequest | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education/${id}/decline`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PUT',
            body: JSON.stringify({ comment }),
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Request declined.")
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error declining Continuing Education request");
        return null;
    }
}

export const updateContinuingEducationRequest = async (id: string, vals: ContinuingEducationFields): Promise<ContinuingEducationRequest | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education/${id}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PUT',
            body: JSON.stringify(vals),
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Continuing Education request resubmitted.")
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error resubmitting Continuing Education request");
        return null;
    }
}

export const deleteContinuingEducationRequest = async (id: string): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/continuing-education/${id}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'DELETE',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Request deleted.")
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error deleting Continuing Education request");
        return false;
    }
}

export const getMyContinuingEducationBudget = async ():
Promise<ContinuingEducationBudgetSummary | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/continuing-education/my-budget`, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const { data } = await res.json();
        return data; 
    } catch (err: any) {
        toast.error(err.message ?? "Error getting Continuing Education budget");
        return null; 
    }
}
